import { getCurrentInstance, onMounted, ref, type Ref } from 'vue'

import TermAcknowledgementFormService from '@/services/clubs/termAcknowledgementFormService'
import { handleRequest } from '@/utils/handleRequest';

interface CacheEntry {
  termAcknowledgementForm: Ref<any>
  loaded: boolean
  inFlight: Promise<any> | null
}

// Shared per term acknowledgement form id so the show/success route guards'
// fetch is reused by the mounted view instead of refetching the same record.
const cache = new Map<string, CacheEntry>()

function entryFor(id: string): CacheEntry {
  let entry = cache.get(id)
  if (!entry) {
    entry = { termAcknowledgementForm: ref(null), loaded: false, inFlight: null }
    cache.set(id, entry)
  }
  return entry
}

// Route-guard policy for a single term acknowledgement form. The show
// endpoint itself calls `authorize term_acknowledgement_form, :show?`, so a
// fetch failure (403) already reflects the access decision — the guard only
// needs to check whether the fetch succeeded.
//
// Also covers the draft-resume route: restore_draft authorizes via
// DraftPolicy#restore? (record.user_id == user.id), so a 403 there means the
// draftId in the URL belongs to someone else. Both endpoints resolve the
// formatted record directly rather than a {success} envelope — only a
// 403/network failure routes through tryCatch's error branch.
export function useTermAcknowledgementFormRoutePolicy(id: string | number) {
  const service = TermAcknowledgementFormService.create()

  const entry = entryFor(String(id))
  const termAcknowledgementForm = entry.termAcknowledgementForm

  const fetchTermAcknowledgementForm = async ({ force = true } = {}) => {
    if (!force && entry.loaded) return { success: true, data: termAcknowledgementForm.value }
    if (entry.inFlight) return await entry.inFlight

    entry.inFlight = handleRequest(
      () => service.getTermAcknowledgementForm(id),
      {
        onSuccess: (form: any) => {
          termAcknowledgementForm.value = form
          entry.loaded = true
        }
      }
    )

    try {
      return await entry.inFlight
    } finally {
      entry.inFlight = null
    }
  }

  const canShow = () => entry.loaded

  const canRestoreDraft = async () => {
    const result: any = await handleRequest(() => service.restoreDraft(id))
    return result?.success !== false
  }

  // Auto-fetch only when used inside a component; route guards call
  // fetchTermAcknowledgementForm() explicitly and have no instance to mount against.
  if (getCurrentInstance()) {
    onMounted(async () => await fetchTermAcknowledgementForm({ force: false }))
  }

  return {
    termAcknowledgementForm,
    fetchTermAcknowledgementForm,
    canShow,
    canRestoreDraft
  }
}
